import { Component, OnInit, ViewChild, Input, EventEmitter, OnDestroy, SimpleChanges, OnChanges, ChangeDetectorRef, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, Subscription, combineLatest, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EupRoutesService } from '../../core/eupRoutes.service';
import { RouterInterceptorService } from '../../core/routerInterceptor.service';
import { SettingsModal } from '../../settings/settings.component';
import { Utils } from '../utils.service';
import { GlobalSettingsService, ContextParams, GlobalSettings } from '../../core/globalSettings.service';
import { ChangeTitlePayload, Company, Contact } from '../generalInterfaces';
import { AccessibilityService } from '../accessibility.service';
import { DownloadNotificationComponent } from '../downloadNotification/downloadNotification.component';
import { AuthService, LoginDetails } from 'app/services/authentication/auth.service';
import { LocalStorageService } from '@core/localStorage.service';
import { Consts } from '@shared/consts';
import { SoftwareOptionsForCompany, FeatureToggle, RoleTypeEnum } from '@shared/enums';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { AppConfigService } from 'app/services/appConfig/appConfigService';
import { UserSettingsService } from '../../services/userSettings/userSettings.service';
import { TimberService } from '@logging/timber.service';
import { BiObjectType } from '@logging/interfaces/bi.enums';
import { PracticeManagementAccessibilityService } from '@shared/practice-management-accessibility.service';
import { StickyHeaderService } from '../../services/stickyHeaderService/stickyHeader.service';
import { RxService } from 'app/rx/services/rx.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { ExportRequest } from '@shared/orthoCadExport/orthoCadExport.service';
import { OrthoCadExportService } from '@shared/orthoCadExport/orthoCadExport.service';
import { RestorativeExportService } from '@shared/restorativeExport/restorativeExport.service';
import { BaseDestroyable } from '@core/base-destroyable';
import { Order } from 'app/doctors/orders/orders.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { KnownComponentsNames } from '@core/components-names';
import { ShowAllService } from '@shared/show-all-toggle/show-all.service';
import { CookieService } from '@core/cookie.service';
import { PlatformLoginService } from 'app/services/platform-login/platform-login.service';
import { SEARCH_FILTER_STORAGE_KEY } from 'assets/consts';
import { CommunicationService } from 'app/services/client-communication/communication.service';
import { NavigationQuery } from 'app/services/shell/application-navigation/state/navigation-query';


@Component({
	selector: 'eup-sticky-header',
	templateUrl: './stickyHeader.component.html',
	styleUrls: ['./stickyHeader.component.scss'],
})
export class StickyHeaderComponent extends BaseDestroyable implements OnInit, OnDestroy, OnChanges {
	BiObjectType = BiObjectType; // Expose the enum to the template

	public static readonly homeImage = 'home46';
	public static readonly homeLink = 'home';
	private readonly backImage = 'backButton46';
	private rolePath: string;
	private storage: Storage;
	isLogoutOpen = false;
	isSettingsMenuOpen = false;
	isLinksMenuOpen = false;
	isDoctorsMenuOpen = false;
	isCompaniesMenuOpen = false;
	enableLabConnect = false;
	enableAccountManagement = false;
	enablePracticeConnectionsTab = false;
	enablePracticeManagement = false;
	isPreSelectCompany = false;
	companies: Company[];
	filteredCompanies: Company[];
	doctors: Contact[];
	selectedCompany: Company;
	selectedDoctor: Contact;
	titleLogo: string = '';
	shouldEnableCaptureTool = false;
	shouldEnableDownloadScreenshots = false;
	shouldShowCaptureTool: boolean;
	isDrawingToolAllowed: boolean;
	showSeparator = false;

	homeLinkClickSubscription: Subscription;
	notificationSub: Subscription;
	screenshotsSub: Subscription;
	downloadScreenshotSub: Subscription;
	contextChangeSub: Subscription;

	searchCompanyText = '';
	showCompaniesFiltering = false;
	enableAnalytics = false;
	analyticsMidcFf = false;
	analyticsMatFf = false;
	window: any;
	routeHasLogin = false;
	isChatBotVisible = false;
	isChatBotFFEnabled = false;
	isLabConnectFFEnabled = false;
	isAmUiEnabled = false;
	selfServiceEnabled = false;
	useIteroPlatformLogin = false;
	order: Order;

	@Input() titleText = '';
	dynamicTitleText = this.titleText;
	@Input() image = StickyHeaderComponent.homeImage;
	@Input() backLink = StickyHeaderComponent.homeLink;
	@Input() backToLastPage = null;
	@Input() disableContactSelection = false;
	@Input() disableDoctorSelection = false;
	@Input() hideContactSelection = false;
	@Input() isLongTitle = false;
	@Input() uponHomeLinkClickCallback: Function;
	@Input() homeLinkClickEvent: EventEmitter<any>;
	@Input() disableSiteNavigation = 'false';
	@Input() isBackLinkAbsoluteUrl = 'false';
	@Input() shouldAddRoleToBackLink = 'true';
	@Input() isVerticalMenu = false;
	@Input() insideContent = false;
	@Input() showScreenshots = false;
	@Input() showDownloadScreenshots = false;
	@Input() showExport = false;
	@Input() backIcon = false;
	@Input() isHidden = false;
	@Input() noShadow = false;
	@Input() shouldCreateScreenshotManager = false;


	@ViewChild(SettingsModal) settingsModal!: SettingsModal;
	@ViewChild(DownloadNotificationComponent) downloadNotification: DownloadNotificationComponent;

	constructor(
		private router: Router,
		public globalSettingsService: GlobalSettingsService,
		private utils: Utils,
		private routerInterceptor: RouterInterceptorService,
		private eupRoutesService: EupRoutesService,
		private route: ActivatedRoute,
		private accessibilityService: AccessibilityService,
		private authService: AuthService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		localStorageService: LocalStorageService,
		public softwareOptionsService: SoftwareOptionsService,
		private appConfigService: AppConfigService,
		private userSettingsService: UserSettingsService,
		private timberService: TimberService,
		private labConnectAccessibilityService: PracticeManagementAccessibilityService,
		private stickyHeaderService: StickyHeaderService,
		private rxService: RxService,
		private shellContextService: ShellContextService,
		private readonly communicationService: CommunicationService,
		private applicationNavigationService: ApplicationNavigationService,
		private readonly showAllService: ShowAllService,
		private cookieService: CookieService,
		private cdr: ChangeDetectorRef,
		private elementRef: ElementRef,
		private platformLoginService: PlatformLoginService,
		private orthoCadExportService: OrthoCadExportService,
		private restorativeExportService: RestorativeExportService,
		private navigationQuery: NavigationQuery
	) {
		super();
		this.storage = localStorageService.storage;
	}

	get hasFullOrdersVisibility(): boolean {
		return this.settings?.hasFullOrdersVisibility;
	}

	get username(): string {
		return this.settings?.username;
	}

	get disableSiteNavigationFunc(): boolean {
		return this.disableSiteNavigation === 'true';
	}

	get shouldAddRoleToBackLinkFunc(): boolean {
		return this.shouldAddRoleToBackLink === 'true';
	}

	get isCompanyDropdownDisabled(): boolean {
		return this.disableContactSelection || this.companies.length === 1;
	}

	get isDoctorDropdownDisabled(): boolean {
		return (
			!this.hasFullOrdersVisibility ||
			this.disableContactSelection ||
			this.doctors.length === 1 ||
			this.disableSiteNavigationFunc ||
			this.disableDoctorSelection
		);
	}
	private get settings(): GlobalSettings {
		return this.globalSettingsService.get();
	}

	onTakeScreenshot() {
		this.stickyHeaderService.triggerScreenshot();
	}

	clickAnalytics() {
		this.router.navigateByUrl('/analytics');
	}

	toggleLogoutMenu() {
		this.isLogoutOpen = !this.isLogoutOpen;
	}

	hideLogoutMenu() {
		this.isLogoutOpen = false;
	}

	toggleSettingsMenu() {
		this.isSettingsMenuOpen = !this.isSettingsMenuOpen;
	}

	hideSettingsMenu() {
		this.isSettingsMenuOpen = false;
	}

	toggleLinksMenu() {
		this.isLinksMenuOpen = !this.isLinksMenuOpen;
	}

	hideLinksMenu() {
		this.isLinksMenuOpen = false;
	}

	ngOnDestroy(): void {
		this.utils.clearObservablesSubscriptions(this);
		this.titleLogo = '';
		this.titleText = '';
		this.stickyHeaderService.resetSubjects();
		super.ngOnDestroy();
	}

	hideDoctorMenu(): void {
		this.isDoctorsMenuOpen = false;
	}

	hideCompaniesMenu(): void {
		this.isCompaniesMenuOpen = false;
	}

	hideDoctorAndCompaniesMenu(): void {
		this.isDoctorsMenuOpen = false;
		this.isCompaniesMenuOpen = false;
	}

	companiesMenuToggled(isOpened: boolean): void {
		if (isOpened) {
			this.hideLinksMenu();
			this.hideLogoutMenu();
			this.downloadNotification.onClose();
		}
		this.clearCompaniesSearch(isOpened);
		this.isCompaniesMenuOpen = isOpened;
	}

	doctorsMenuToggled(isOpened: boolean): void {
		if (isOpened) {
			this.hideLinksMenu();
			this.hideLogoutMenu();
			this.downloadNotification.onClose();
		}

		this.isDoctorsMenuOpen = isOpened;
	}

	ngOnInit(): void {
		this.shellContextService
			.getOrder()
			.pipe(
				takeUntil(this.componentAlive$),
				filter((order) => !!order),
				tap((order: Order) => {
					this.order = order;
					this.shouldEnableDownloadScreenshots = order.isCaptureFileExist;
				})
			)
			.subscribe();

		this.showAllService.isShowAllToggleEnabled$
			.pipe(
				tap((isShowAllToggleEnabled) => {
					this.disableDoctorSelection = isShowAllToggleEnabled;
				})
			)
			.subscribe();

		const selectedCompanyId = +this.settings.selectedCompanyId;
		this.companies = this.settings.companies;
		this.filteredCompanies = this.settings.companies;
		this.rolePath = this.globalSettingsService.rolePath();
		this.showCompaniesFiltering = this.companies.length >= this.appConfigService.appSettings.minimumCompaniesForFiltering;
		if (this.shouldAddRoleToBackLinkFunc) {
			this.backLink = this.rolePath + '/' + this.backLink;
		}
		this.selectedCompany = this.getCompanyById(selectedCompanyId);
		this.showAllService.updateHasFullOrdersVisibility(this.selectedCompany?.currentUserHasFullOrdersVisibility);

		this.redirectIfRequired(this.selectedCompany);
		this.fillDoctors(selectedCompanyId);
		this.contextChangeSub = this.globalSettingsService.contextChanged
			.pipe(takeUntil(this.componentAlive$))
			.subscribe((params: ContextParams) => {
				if (!params.companyId || !params.doctorId) {
					return;
				}
				this.updateCompany(this.getCompanyById(+params.companyId));
				this.onDoctorChanged(this.getDoctorById(+params.doctorId));
				this.enableAnalytics = this.shouldShowAnalytics();
			});
		this.featuresToggleSettingsService
			.getFeaturesToggleSettings()
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(() => {
				this.analyticsMidcFf = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.IteroAnalyticsMIDC, false);
				this.analyticsMatFf = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.IteroAnalyticsMAT, false);
				this.isLabConnectFFEnabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.LabConnectTab, false);
				this.isAmUiEnabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.AMUIEnabled, false);
				this.enableAnalytics = this.shouldShowAnalytics();
				this.useIteroPlatformLogin = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.UseIteroPlatformLogin, false);
				this.selfServiceEnabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.SelfServiceUIEnabled, false);
			});
		combineLatest([
			this.labConnectAccessibilityService.labConnectAvailability$,
			this.labConnectAccessibilityService.accountManagementAvailability$,
			this.labConnectAccessibilityService.practiceConnectionsTabAvailability$
		]).pipe(takeUntil(this.componentAlive$),
			tap(([labConnectAvailability, accountManagementAvailability, practiceConnectionsTabAvailability]) => {
				this.enableLabConnect = labConnectAvailability;
				this.enableAccountManagement = accountManagementAvailability;
				this.enablePracticeConnectionsTab = practiceConnectionsTabAvailability;
				this.enablePracticeManagement = (labConnectAvailability || accountManagementAvailability || practiceConnectionsTabAvailability) && this.selectedCompany?.currentUserIsAccountAdmin;
			})
		).subscribe();

		// when this property is set to true - override image and back link
		// according to previous url
		const previousRoute = this.routerInterceptor.previousRoute;
		if (this.backToLastPage === 'true' || this.backToLastPage === true) {
			var loginRoutes = this.platformLoginService.getLoginRoutes();
			// if no previous url exists (e.g. user entered url in a new tab)
			// or the previous url is home so back button should link to home page
			// or user was redirected to a page from straight from login
			if (
				!previousRoute ||
				previousRoute === '/' + this.rolePath + '/' + StickyHeaderComponent.homeLink ||
				loginRoutes.includes(previousRoute) ||
				loginRoutes.some(route => previousRoute?.split('?')[0].includes(route)) ||
				previousRoute === '/'
			) {
				this.setDefaultLinkAndImage();
			} else {
				// a previous url exists and it is not the home page so back button should link
				// to the last page that the user was at
				this.backLink = previousRoute.slice(1);
				this.image = this.backImage;
				this.backIcon = true;
			}
		} else if (this.backIcon) {
			this.image = this.backImage;
		} else if (this.backToLastPage === 'false') {
			this.setDefaultLinkAndImage();
		}

		this.preselectCompany();
		this.checkRouteHasLogin(previousRoute);
		this.subscribeToChangeTitle();
		this.subscribeToScreenshotsEnabled();
		this.subscribeToDownloadScreenshotsEnabled();
		this.subscribeToChangeButtonsVisibilityEvent();
		this.initTools(selectedCompanyId);
	}

	ngAfterViewInit() {
		document.documentElement.style.setProperty('--header-height', `${this.elementRef.nativeElement.offsetHeight}px`);
		this.notificationSub = this.userSettingsService.isNotificationsEnabled.subscribe((isNotificationsEnabled) => {
			this.openNotificationsSettings(isNotificationsEnabled);
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.titleText && changes.titleText.currentValue !== changes.titleText.previousValue) {
			this.dynamicTitleText = this.titleText;
			this.handleLogo();
		}
		if (changes.showScreenshots && changes.showScreenshots.currentValue !== changes.showScreenshots.previousValue) {
			this.shouldShowCaptureTool = this.showScreenshots;
			this.showDownloadScreenshots = this.showScreenshots;
			this.showExport = this.showScreenshots;
		}
	}

	handleLogo() {
		const savedLogo = sessionStorage.getItem(Consts.Storage.Logo);
		const currentAppId = this.applicationNavigationService.peekTransition()?.navigatedToAppId;

		if (savedLogo && this.isAohsLaunchedApp(currentAppId)) {
			this.titleLogo = savedLogo;
			this.showSeparator = true;
		} else {
			this.titleLogo = '';
			this.stickyHeaderService.resetLogo();
			this.showSeparator = false;
		}
	}

	/**
	 * Returns true when the given appId is one of the apps that AOHS launches
	 * as a child and that should display the AOHS logo + separator in the title.
	 */
	private isAohsLaunchedApp(appId: string | undefined): boolean {
		if (!appId) {
			return false;
		}
		const aohsLaunchedApps = [
			KnownComponentsNames.IosimPlus,
			KnownComponentsNames.IteroVisitReport,
			KnownComponentsNames.ImageHub,
			KnownComponentsNames.OldIosimPlus,
		];
		return aohsLaunchedApps.includes(appId);
	}

	/**
	 * Returns true when the current navigation transition stack contains AOHS
	 * as a launcher — meaning we are inside an app that was opened from AOHS.
	 */
	private isOpenedFromAohs(): boolean {
		return this.navigationQuery.transitionsStack
			.some(t => t.navigatedFromAppId === KnownComponentsNames.AohsComponent);
	}

	private subscribeToScreenshotsEnabled() {
		this.screenshotsSub = this.stickyHeaderService.screenshotsEnabled$
			.pipe(
				tap((screenshotsEnabled) => {
					this.shouldEnableCaptureTool = screenshotsEnabled;
				})
			)
			.subscribe();
	}

	private subscribeToChangeButtonsVisibilityEvent() {
		this.stickyHeaderService.changeButtonsVisibility$
			.pipe(
				tap((buttonsStatus) => {
					if (buttonsStatus && buttonsStatus.length > 0) {
						buttonsStatus.forEach((button) => {
							this.insideContent = true;
							switch (button['buttonId']) {
								case 'export-btn':
									this.showExport = button['visible'];
									break;
								case 'takeScreenshot-btn':
									this.shouldShowCaptureTool = button['visible'];
									break;
								case 'downloadScreenshot-btn':
									this.showDownloadScreenshots = button['visible'];
									break;

								default:
									break;
							}
						});
					}
				})
			)
			.subscribe();
	}

	private subscribeToDownloadScreenshotsEnabled() {
		this.downloadScreenshotSub = this.stickyHeaderService.downloadScreenshotsEnabled$
			.pipe(
				tap((downloadScreenshotsEnabled) => {
					this.shouldEnableDownloadScreenshots = this.order?.isCaptureFileExist || downloadScreenshotsEnabled;
				})
			)
			.subscribe();
	}

	private initTools(companyId: number) {
		this.softwareOptionsService
			.getSnapshotSoftwareOptions(companyId)
			.pipe(
				takeUntil(this.componentAlive$),
				tap((response) => {
					this.shouldShowCaptureTool = this.showScreenshots && !response?.shouldDisableCapture;
					this.isDrawingToolAllowed = !response?.shouldDisableDrawingTool;
				})
			)
			.subscribe();
	}

	private subscribeToChangeTitle() {
		this.stickyHeaderService.titleChanged$
			.pipe(
				takeUntil(this.componentAlive$),
				filter(() => !this.isHidden),
				tap((titleInfo: ChangeTitlePayload) => this.applyTitleChange(titleInfo))
			)
			.subscribe();
	}

	private applyTitleChange(titleInfo: ChangeTitlePayload): void {
		if (!titleInfo.title) {
			return;
		}

		const currentAppId = this.applicationNavigationService.peekTransition()?.navigatedToAppId;

		// Case 1: a child app launched from AOHS (visit report, iosim pro, image hub).
		// The child sends its own title but no logo — we pull the AOHS logo from
		// sessionStorage (set when AOHS emitted its own change-title) and show it
		// alongside the child's title with a separator.
		if (this.isOpenedFromAohs() && this.isAohsLaunchedApp(currentAppId)) {
			const savedLogo = sessionStorage.getItem(Consts.Storage.Logo);
			this.dynamicTitleText = titleInfo.title;
			this.titleLogo = savedLogo ?? '';
			this.showSeparator = true;
			return;
		}

		// Case 2: AOHS itself (or any app) sends a title containing the <LOGO>
		// placeholder together with a logo URL — the legacy behaviour that AOHS uses
		// when displaying its own title.
		if (titleInfo.title.includes('<LOGO>') && titleInfo.logo) {
			const cleanTitle = titleInfo.title.replace('<LOGO>', '').trim();
			if (cleanTitle.includes('|')) {
				this.dynamicTitleText = cleanTitle.split('|')[1].trim();
				this.showSeparator = true;
			} else {
				this.dynamicTitleText = cleanTitle;
				this.showSeparator = false;
			}
			this.titleLogo = titleInfo.logo;
			sessionStorage.setItem(Consts.Storage.Logo, titleInfo.logo);
			return;
		}

		// Case 3: plain text title, no logo involved.
		this.dynamicTitleText = titleInfo.title.replace('<LOGO>', '').trim();
		this.titleLogo = '';
		this.showSeparator = false;
		this.stickyHeaderService.resetLogo();
	}

	logout(): void {
		this.authService.logout().pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	updateCompany(company: Company): void {
		if (this.selectedCompany.id === company.id) {
			return;
		}
		const useIteroPlatformLoginFFEnabled = this.useIteroPlatformLogin && this.platformLoginService.iteroPlatformLoginUsed();

		if (!useIteroPlatformLoginFFEnabled) {
			this.switchCompanyLegacy(company);
			return;
		}

		this.switchCompanyWithDifferentRegion(company);
	}

	private switchCompanyLegacy(company: Company): void {
		this.redirectIfRequired(company);
		this.selectedCompany = company;
		this.globalSettingsService.setCompanyAndDefaultDoctor(company.id)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe();
		this.fillDoctors(company.id);
		this.initTools(company.id);
	}

	private switchCompanyWithDifferentRegion(company: Company): void {
		this.updateRoutesIfRequired(company)
			.pipe(
				takeUntil(this.componentAlive$),
				switchMap(() =>
					this.globalSettingsService.setCompanyAndDefaultDoctor(company.id)
				),
				tap(() => {
					this.selectedCompany = company;
					this.communicationService.setRegion(company.region);
					this.shellContextService.updateContext((context) => {
						context.bizCtx.region = company.region;
					});
				}),
				catchError((error) => {
					this.timberService.error('Failed to switch company', { module: 'StickyHeaderComponent', error });
					this.switchCompanyLegacy(company);
					return EMPTY;
				})
			)
			.subscribe({
				next: () => {
					this.fillDoctors(company.id);
					this.initTools(company.id);
				}
			});
	}

	onCompanyChanged(company: Company): void {
		this.showAllService.updateHasFullOrdersVisibility(company.currentUserHasFullOrdersVisibility);
		sessionStorage.setItem(SEARCH_FILTER_STORAGE_KEY, '');
		this.isPreSelectCompany = false;
		this.updateCompany(company);
	}
	private openNotificationsSettings(isNotificationsEnabled) {
		this.route.data
			?.pipe(
				takeUntil(this.componentAlive$),
				filter((data) => data['openNotificationsSettings'])
			)
			.subscribe(() => {
				if (this.isPreSelectCompany && isNotificationsEnabled) {
					this.isPreSelectCompany = false;
					this.settingsModal.show(this.settingsModal.notificationsSettingsModal);
					this.cdr.detectChanges();
				}
			});
	}

	onDoctorChanged(doctor: Contact): void {
		sessionStorage.setItem(SEARCH_FILTER_STORAGE_KEY, '');
		if (doctor === undefined || this.selectedDoctor.contactId === doctor.contactId) {
			return;
		}

		this.selectedDoctor = doctor;
		// to ensure the HTTP interceptor reads the correct doctor ID
		const settings = this.globalSettingsService.get();
		settings.selectedDoctorId = doctor.contactId;
		localStorage.setItem(Consts.Storage.Settings, JSON.stringify(settings));
		this.globalSettingsService.setDoctor(doctor.contactId);

		this.globalSettingsService
			.setSelectedDoctor(this.selectedCompany.id, this.selectedDoctor.contactId)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe((_) => {
				this.timberService.sessionTagsEvent({ tags: { userProfile: doctor.contactId?.toString() } });
			});
	}

	onImageLinkClick(): void {
		if (this.uponHomeLinkClickCallback) {
			this.uponHomeLinkClickCallback();
		}
		if (this.homeLinkClickEvent && !this.homeLinkClickSubscription) {
			this.homeLinkClickSubscription = this.homeLinkClickEvent.subscribe((res: any) => {
				this.applicationNavigationService.handleBackClicked();
			});
		} else if (this.backIcon) {
			this.applicationNavigationService.handleBackClicked();
		} else {
			this.applicationNavigationService.reset();
			this.applicationNavigationService.navigateToHomePage();
		}
	}

	onLearningCenterClick(): void {
		this.router.navigateByUrl(`/${this.rolePath}/learningcenter`);
	}

	onPracticeManagementLinkClick(): void {
		const currentUrl = this.router.url;
		let link: string;
		if (this.isAmUiEnabled) {
			if (this.selfServiceEnabled) {
				this.applicationNavigationService.navigateToStandardRemoteApplication(Consts.selfServiceAppId);
			} else {
				link = `/mfe-wrapper/${Consts.AccountManagement}`;
				if (!currentUrl.startsWith(link)) {
					this.router.navigate([link]);
				}
			}
		} else {
			link = this.enableAccountManagement ? '/practice-management/info' : '/practice-management/mylabs';
			this.router.navigate([link]);
		}
	}

	onDownloadScreenshotClick() {
		this.rxService.getExportGalleryFile(this.order.id.toString(), this.order?.patientName, this.order?.guid);
	}

	onExportClick(event: Event) {
		if (this.order != null && this.order.canWebExport) {
			event.stopPropagation();
			event.preventDefault();

			const request: ExportRequest = {
				orderDetailsId: +this.order.lastDetailsId,
				orderId: +this.order.id,
			};

			if (this.order.isOrthoOrder === true) {
				this.orthoCadExportService.openOrthoCadExportModal(this.order.caseTypeId.toString(), request);
			} else {
				this.restorativeExportService.openRestorativeExportModal(this.order.caseTypeId.toString(), request);
			}
		}
	}

	private getDoctorById(id: number): Contact {
		return this.doctors.filter((c: Contact) => c.contactId === id)[0];
	}

	private getCompanyById(id: number): Company {
		return this.settings.companies.filter((c: Company) => c.id === id)[0];
	}

	private fillDoctors(companyId: number): void {
		this.doctors = [
			{
				contactId: this.settings.contactId,
				contactName: this.settings.username,
			},
		];

		this.selectedDoctor = this.doctors[0];

		if (this.hasFullOrdersVisibility) {
			this.globalSettingsService
				.getContactsForCompany(companyId)
				.pipe(takeUntil(this.componentAlive$))
				.subscribe((data: Contact[]) => {
					this.doctors = data;

					const doctorsMap = data.reduce((acc, { contactId, contactName }) => {
						acc[contactId] = contactName;
						return acc;
					}, {});

					this.showAllService.updateDoctorsMap(doctorsMap);
					if (this.doctors.filter((c: Contact) => c.contactId === +this.settings.selectedDoctorId).length > 0) {
						this.selectedDoctor = this.getDoctorById(+this.settings.selectedDoctorId);
					}
				});
		}
	}

	private preselectCompany() {
		this.route.queryParams.pipe(takeUntil(this.componentAlive$)).subscribe((params) => {
			if (!params['companyid']) {
				return;
			}
			this.isPreSelectCompany = true;
			this.updateCompany(this.getCompanyById(+params['companyid']));
		});
	}

	updateRoutes(force: boolean = false): Observable<any> {
		return this.eupRoutesService.doAfterInit(() => {
			return this.eupRoutesService.getRoutes();
		}, force);
	}

	private redirectIfRequired(company: Company): void {
		if (!company.handlingEupUrl) return;

		if (company.handlingEupUrl.endsWith('/')) {
			company.handlingEupUrl = company.handlingEupUrl.slice(0, -1);
		}

		const myIteroBffUrl = new URL(this.eupRoutesService.serverUrl);
		const companyMyIteroBffUrl = new URL(company.handlingEupUrl);

		if (myIteroBffUrl.host === companyMyIteroBffUrl.host) return;

		const currentPath = this.accessibilityService.getPathName();
		const currentSearchParams = new URLSearchParams(this.accessibilityService.getCurrentSearch());

		if (this.shouldConcatinateCompany(currentPath)) {
			currentSearchParams.set('companyid', company.id.toString());
		}

		const returnUrl = `${currentPath}?${currentSearchParams.toString()}`;
		const baseUrl = this.accessibilityService.getLocation().origin;
		const queryParams = `returnUrl=${encodeURIComponent(returnUrl)}`;

		this.authService.logout().pipe(
			takeUntil(this.componentAlive$),
			tap(() => {
				this.cookieService.set(Consts.CookiesNames.SWITCHREGIONURL, `${companyMyIteroBffUrl.origin}/`, Number.MAX_SAFE_INTEGER);
				this.cookieService.set(Consts.CookiesNames.RETURNURL, returnUrl, Number.MAX_SAFE_INTEGER);
				this.storage[Consts.Storage.RegionalApiEndpoint] = `${companyMyIteroBffUrl.origin}/`;
				this.platformLoginService.navigateToExternalLogin(baseUrl, queryParams);
			})
		).subscribe();
	}

	private updateRoutesIfRequired(company: Company): Observable<void> {
		if (!company?.handlingEupUrl) {
			return of(void 0);
		}

		const companyHandlingEupUrl = company.handlingEupUrl.endsWith('/')
			? company.handlingEupUrl.slice(0, -1)
			: company.handlingEupUrl;

		const currentRegionalEndpoint = localStorage.getItem(Consts.Storage.RegionalApiEndpoint);
		const currentMyIteroBffUrl = new URL(this.eupRoutesService.serverUrl);
		const companyMyIteroBffUrl = new URL(companyHandlingEupUrl);

		if (currentMyIteroBffUrl.host === companyMyIteroBffUrl.host) {
			return of(void 0);
		}

		this.timberService.info(`Start update routes when company changed.`, { module: 'StickyHeaderComponent' });

		return this.authService.getDetails().pipe(
			switchMap((res: LoginDetails) => {
				const regionKey = `aws-endpoint.${company.region}.Eup`;

				if (!res?.regionsEndpoints || !(regionKey in res.regionsEndpoints)) {
					this.timberService.info(
						`Endpoint for region ${company.region || 'none'} does not exist (key: ${regionKey}).`,
						{ module: 'StickyHeaderComponent' }
					);

					return of(void 0);
				}

				const requiredRegion = res.regionsEndpoints[regionKey];

				this.timberService.info(
					`Current regional endpoint is ${currentRegionalEndpoint || 'none'}, required endpoint is ${requiredRegion || 'none'} for company ${company.id || 'none'}.`,
					{ module: 'StickyHeaderComponent' }
				);

				if (requiredRegion === currentRegionalEndpoint) {
					return of(void 0);
				}

				localStorage.setItem(
					Consts.Storage.RegionalApiEndpoint,
					requiredRegion
				);

				return this.eupRoutesService
					.updateRoutes(true, 'StickyHeaderComponent')
					.pipe(
						switchMap((result) => {
							if (!result) {
								return throwError(
									() => new Error('Failed to update regional routes')
								);
							}
							return of(void 0);
						}),
						catchError((error) => {
							if (currentRegionalEndpoint === null) {
								localStorage.removeItem(Consts.Storage.RegionalApiEndpoint);
							} else {
								localStorage.setItem(Consts.Storage.RegionalApiEndpoint, currentRegionalEndpoint);
							}
							return throwError(() => error);
						})
					);
			})
		);
	}

	private setDefaultLinkAndImage() {
		this.backLink = this.rolePath + '/' + StickyHeaderComponent.homeLink;
		this.image = StickyHeaderComponent.homeImage;
	}

	private shouldConcatinateCompany(urlPathName: string): boolean {
		if (urlPathName.indexOf(`/viewer-wrapper/viewer`) > -1) {
			return false;
		} else {
			return true;
		}
	}

	private shouldShowAnalytics(): boolean {
		const isAdmin = this.selectedCompany.currentUserIsAccountAdmin;
		const softwareOptions = this.globalSettingsService.get()?.companySoftwareOptions;
		const res = this.softwareOptionsService.areSoftwareOptionExist([SoftwareOptionsForCompany.iTeroAnalytics], softwareOptions);
		return isAdmin && this.analyticsMidcFf && (this.analyticsMatFf || res);
	}

	private searchClicked(event: Event) {
		event.stopPropagation();
	}

	private onSearchCompany(event: Event) {
		if (this.searchCompanyText.length > 0) {
			this.filteredCompanies = this.settings.companies.filter((c) =>
				c.name.toLowerCase().includes(this.searchCompanyText.toLowerCase())
			);
		} else {
			this.filteredCompanies = this.companies;
		}
	}

	private clearCompaniesSearch(isDropdwonOpen: boolean) {
		this.searchCompanyText = !isDropdwonOpen ? '' : this.searchCompanyText;
		this.filteredCompanies = !isDropdwonOpen ? this.companies : this.filteredCompanies;
	}

	private checkRouteHasLogin(previousRoute) {
		var loginRoutes = this.platformLoginService.getLoginRoutes();
		if (!!previousRoute && loginRoutes.includes(previousRoute.split('?')[0])) {
			this.routeHasLogin = true;
		}
	}
}
